import styled from "@emotion/styled";
import { Divider, Skeleton, Typography } from "@avenue-8/sidekick-ui";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import type { ContactListItem } from "../contacts.types";
import { ContactsListActionsMenu } from "./ContactsListActionsMenu";
import { RadarMonitoringToggle } from "./RadarMonitoringToggle";

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  cursor: pointer;
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`;

const EmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: ${(p) => p.theme.text.colors.secondary};
`;

const EmptyState = styled.div`
  padding: 1.5rem 1rem;
  text-align: center;
`;

type ContactsMobileListProps = {
  contacts: ContactListItem[];
  isLoading: boolean;
  onViewBriefing: (contactId: string) => void;
  onDeleteContact: (contactId: string, contactName: string) => void;
  pendingDeleteContactId: string | null;
  hasRadarModule: boolean;
  isMonitoredByContactId: Record<string, boolean>;
  isMonitoredSinceByContactId: Record<string, string | null>;
  pendingMonitoringContactIds: Set<string>;
  onToggleMonitoring: (
    contactId: string,
    contactName: string,
    nextValue: boolean,
  ) => void;
};

export const ContactsMobileList = (props: ContactsMobileListProps) => {
  const {
    contacts,
    isLoading,
    onViewBriefing,
    onDeleteContact,
    pendingDeleteContactId,
    hasRadarModule,
    isMonitoredByContactId,
    isMonitoredSinceByContactId,
    pendingMonitoringContactIds,
    onToggleMonitoring,
  } = props;

  if (isLoading && contacts.length === 0) {
    return (
      <div>
        {Array(4)
          .fill(null)
          .map((_, index) => (
            <Row key={index}>
              <Skeleton variant="rectangular" width="100%" height={72} />
            </Row>
          ))}
      </div>
    );
  }

  if (!isLoading && contacts.length === 0) {
    return (
      <EmptyState>
        <Typography variant="body2" py={4}>
          No contacts found
        </Typography>
      </EmptyState>
    );
  }

  return (
    <div>
      {contacts.map((contact, index) => {
        const isDeleting = contact.id === pendingDeleteContactId;
        return (
          <div key={contact.id}>
            <Row
              onClick={() => !isDeleting && onViewBriefing(contact.id)}
              style={{
                opacity: isDeleting ? 0.5 : 1,
                pointerEvents: isDeleting ? "none" : "auto",
              }}
            >
              <TopRow>
                <Typography variant="subtitle1">{contact.name}</Typography>
                <ContactsListActionsMenu
                  onViewBriefingClick={() => onViewBriefing(contact.id)}
                  onDeleteClick={() =>
                    onDeleteContact(contact.id, contact.name)
                  }
                  isDeleteDisabled={pendingDeleteContactId !== null}
                />
              </TopRow>
              <EmailRow>
                <EmailOutlinedIcon fontSize="small" />
                <Typography variant="body2" color="inherit">
                  {contact.email}
                </Typography>
              </EmailRow>
              {hasRadarModule && (
                <div onClick={(e) => e.stopPropagation()}>
                  <RadarMonitoringToggle
                    isMonitored={isMonitoredByContactId[contact.id] ?? false}
                    isMonitoredSince={
                      isMonitoredSinceByContactId[contact.id] ?? null
                    }
                    isTogglePending={pendingMonitoringContactIds.has(
                      contact.id,
                    )}
                    onToggle={(nextValue) =>
                      onToggleMonitoring(contact.id, contact.name, nextValue)
                    }
                  />
                </div>
              )}
            </Row>
            {index < contacts.length - 1 && <Divider />}
          </div>
        );
      })}
    </div>
  );
};
